
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-alpine.css";
import { SquarePen, Trash2, ChevronUp, ChevronDown } from "lucide-react";
import { toast } from "react-toastify";
import ListView from "../../shared/ListView";
import ConfirmModal from "../../shared/ConfirmModal";

// <-- adjust this import path to where your service barrel lives -->
import { useProductList, useProductOperations } from  "../../../../services/redux/api/components";

const LifeProductMasterList = () => {
  const navigate = useNavigate();
  const gridRef = useRef(null);

  // server-driven data
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  // local UI / selection state
  const [rowData, setRowData] = useState([]); // mapped rows for grid
  const [loading, setLoading] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteMode, setDeleteMode] = useState("single");
  const [isOpen, setIsOpen] = useState(false);
  const [selectedNames, setSelectedNames] = useState([]);
  const selectedIdsRef = useRef([]);
  const [allSelected, setAllSelected] = useState(false);
  const [preserveSelection, setPreserveSelection] = useState(false);

  // Filter & sort states (these are forwarded to API as searchValue)
  const [filterColumn, setFilterColumn] = useState("productName");
  const [filterValue, setFilterValue] = useState("");
  const [columnOptions, setColumnOptions] = useState([]);
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);

  // column defs
  const [columnDefs, setColumnDefs] = useState([]);

  // API hooks
  // useProductList returns the RTK Query hook result (data, isLoading, error, etc.)
  const productQuery = useProductList(currentPage, itemsPerPage, {
    // pass both searchTerm (column) and searchValue when filter applied
    searchTerm: isFilterApplied ? filterColumn : null,
    searchValue: isFilterApplied ? filterValue : null,
  });

  const { data: apiResponse, isLoading: apiLoading, error: apiError } = productQuery;
  // apiResponse shape (based on transformResponse in endpoints): { data: [...], totalCount }
  const totalCount = apiResponse?.totalCount ?? 0;

  // Operations (delete, bulk delete, save/update are available from this hook)
  const {
    saveProduct,
    updateProduct,
    deleteProduct,
    bulkDeleteProducts,
    // meta info lives on the mutation objects if you need them
  } = useProductOperations();

  // set column options for filter dropdown
  useEffect(() => {
    const options = [
      { value: "insurer", label: "Insurer" },
      { value: "productName", label: "Product Name" },
      { value: "productShortName", label: "Product Short Name" },
      { value: "minSum", label: "Min Sum " },
      { value: "maxSum", label: "Max Sum " },
      { value: "minAge", label: "Min Age" },
      { value: "maxAge", label: "Max Age" },
      { value: "paymentModes", label: "Payment Mode" },
    ];
    setColumnOptions(options);
  }, []);

  // map API rows to grid-friendly rows whenever apiResponse changes
  useEffect(() => {
    if (!apiResponse) {
      setRowData([]);
      return;
    }

    const mapped = (apiResponse.data || []).map((p) => ({
      // map server fields to UI fields your grid uses
      id: p.ProductID ?? p.ProductId ?? p.id,
      insurer: p.InsurerName ?? p.InsurerID ?? "", // if you have InsurerName use it; otherwise id
      productName: p.ProductName ?? "",
      productShortName: p.ProductShortName ?? "",
      minSum: p.MinSum ?? p.MinimumSum ?? null,
      maxSum: p.MaxSum ?? p.MaximumSum ?? null,
      minAge: p.MinAge ?? null,
      maxAge: p.MaxAge ?? null,
      paymentMode: p.PaymentModes ?? p.PaymentMode ?? "",
      // keep original object if needed
      __raw: p,
    }));

    setRowData(mapped);
  }, [apiResponse]);

  // reflect API loading state
  useEffect(() => setLoading(!!apiLoading), [apiLoading]);

  // update header components when sort changes
  const createHeaderComponent = (field, displayName) => {
    return function CustomHeader() {
      const isSorted = sortField === field;
      const isAsc = sortDirection === "asc";

      return (
        <div className="cursor-pointer hover:text-brandRed flex items-center" onClick={() => handleSort(field)}>
          {displayName}
          {isSorted && (
            <span className="ml-1">{isAsc ? <ChevronUp size={16} /> : <ChevronDown size={16} />}</span>
          )}
        </div>
      );
    };
  };

  useEffect(() => {
    if (columnDefs.length > 0) {
      const updated = columnDefs.map((col) =>
        col.headerComponent ? { ...col, headerComponent: createHeaderComponent(col.field, col.headerName) } : col
      );
      setColumnDefs(updated);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortField, sortDirection]);

  // column definitions (same as before)
  useEffect(() => {
    const createColumnDefs = () => [
      {
        headerCheckboxSelection: true,
        checkboxSelection: true,
        width: 50,
        suppressMenu: true,
      },
      {
        headerName: "Insurer",
        field: "insurer",
        flex: 1,
        headerComponent: createHeaderComponent("insurer", "Insurer"),
        suppressMenu: true,
      },
      {
        headerName: "Product Name",
        field: "productName",
        flex: 1,
        headerComponent: createHeaderComponent("productName", "Product Name"),
        suppressMenu: true,
      },
      {
        headerName: "Product Short Name",
        field: "productShortName",
        flex: 1,
        headerComponent: createHeaderComponent("productShortName", "Product Short Name"),
        suppressMenu: true,
      },
      {
        headerName: "Min Sum",
        field: "minSum",
        flex: 1,
        headerComponent: createHeaderComponent("minSum", "Min Sum"),
        suppressMenu: true,
      },
      {
        headerName: "Max Sum",
        field: "maxSum",
        flex: 1,
        headerComponent: createHeaderComponent("maxSum", "Max Sum"),
        suppressMenu: true,
      },
      {
        headerName: "Min Age",
        field: "minAge",
        flex: 1,
        headerComponent: createHeaderComponent("minAge", "Min Age"),
        suppressMenu: true,
      },
      {
        headerName: "Max Age",
        field: "maxAge",
        flex: 1,
        headerComponent: createHeaderComponent("maxAge", "Max Age"),
        suppressMenu: true,
      },
      {
        headerName: "Payment Mode",
        field: "paymentMode",
        flex: 1,
        headerComponent: createHeaderComponent("paymentMode", "Payment Mode"),
        suppressMenu: true,
      },
      {
        headerName: "Action",
        field: "action",
        headerClass: "action-header",
        cellRenderer: (params) => (
          <div className="flex space-x-2 mt-1">
            <button
              className="edit-button"
              onClick={() => navigate(`/life-product/edit/${params.data.id}`)}
              title="Edit"
              onMouseDown={(e) => e.preventDefault()}
            >
              <SquarePen color="#787474" size={18} className="mt-1" />
            </button>
            <button
              className="delete-button"
              onClick={(e) => {
                e.stopPropagation();
                // open modal for single item and pass its id/name so modal lists the item
                setDeleteId(params.data.id);
                setDeleteMode("single");
                onOpen({ ids: [params.data.id], names: [params.data.productName || ""] });
              }}
              title="Delete"
              onMouseDown={(e) => e.preventDefault()}
            >
              <Trash2 color="#787474" size={18} className="mt-1" />
            </button>
          </div>
        ),
        sortable: false,
      },
    ];

    setColumnDefs(createColumnDefs());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // handle selection changes from ag-grid
  const onSelectionChanged = () => {
    if (isOpen || !gridRef.current?.api) return;

    try {
      const selectedNodes = gridRef.current.api.getSelectedNodes() || [];
      const totalDisplayedRows = gridRef.current.api.getDisplayedRowCount() || 0;

      selectedIdsRef.current = selectedNodes.map((node) => node.data.id);
      setSelectedNames(selectedNodes.map((node) => node.data.productName || ""));

      const multi = selectedNodes.length > 1;
      setDeleteMode(multi ? "multi" : "single");

      // Don't auto-open confirmation when selecting rows (including select-all).
      // Instead, show an action toolbar with a Delete button (user must confirm).
      if (selectedNodes.length === totalDisplayedRows && totalDisplayedRows > 0) {
        setAllSelected(true);
      } else {
        setAllSelected(false);
      }
    } catch (error) {
      console.error("Error in onSelectionChanged:", error);
    }
  };

  // Open confirm modal. If `payload` provided, use its ids/names instead of reading grid selection.
  const onOpen = (payload) => {
    if (!payload && !gridRef.current?.api) return;

    try {
      if (payload && (payload.ids || payload.names)) {
        selectedIdsRef.current = payload.ids || [];
        setSelectedNames(payload.names || []);
      } else {
        const selectedNodes = gridRef.current.api.getSelectedNodes() || [];
        selectedIdsRef.current = selectedNodes.map((node) => node.data.id);
        setSelectedNames(selectedNodes.map((node) => node.data.productName || ""));
      }

      setPreserveSelection(true);
      setIsOpen(true);
    } catch (error) {
      console.error("Error in onOpen:", error);
    }
  };

  const onClose = () => {
    setIsOpen(false);
    setAllSelected(false);
    setPreserveSelection(false);
  };

  // confirm delete calls API mutation(s)
const confirmDelete = async () => {
  try {
    const ids =
      selectedIdsRef.current && selectedIdsRef.current.length > 0
        ? selectedIdsRef.current
        : deleteId
        ? [deleteId]
        : [];

    if (ids.length === 0) {
      toast.warning("No items selected to delete");
      onClose();
      return;
    }

    // -------------------------
    // SINGLE DELETE
    // -------------------------
    if (ids.length === 1) {
      const response = await deleteProduct(ids[0]).unwrap();

      // if API returns success = false
      if (response?.success === false) {
        // custom FK validation message
        if (response?.message?.includes("REFERENCE constraint")) {
          toast.error("Cannot delete product because it is already used in Life Policy Entry.");
        } else {
          toast.error(response?.message || "Failed to delete product");
        }
        onClose();
        return;
      }

      toast.success("Product deleted successfully");
    }

    // -------------------------
    // BULK DELETE
    // -------------------------
    else {
      const response = await bulkDeleteProducts({ ids }).unwrap();

      if (response?.success === false) {
        if (response?.message?.includes("REFERENCE constraint")) {
          toast.error("Cannot delete product because some products are used in Life Policy Entry.");
        } else {
          toast.error(response?.message || "Failed to delete selected products");
        }
        onClose();
        return;
      }

      toast.success("Selected products deleted successfully");
    }

    onClose();
  } catch (err) {
    console.error("Delete error:", err);

    const errMsg = err?.data?.message || err?.message;

    if (errMsg?.includes("REFERENCE constraint")) {
      toast.error("Cannot delete product because it is already used in Life Policy Entry.");
    } else {
      toast.error(errMsg || "Failed to delete product(s)");
    }
  }
};



  // sorting: we only update local sort state (you may wire to API later)
  const handleSort = async (field) => {
    let direction = "asc";
    if (sortField === field) {
      direction = sortDirection === "asc" ? "desc" : "asc";
    }
    setSortField(field);
    setSortDirection(direction);
    setCurrentPage(1);
    // if backend supports sort, pass sortField/sortOrder in filters to useProductList
  };

  // filter handlers
  const handleFilter = async () => {
    if (!filterValue.trim()) {
      toast.warning("Please enter filter value");
      return;
    }
    setIsFilterApplied(true);
    setCurrentPage(1);
  };

  const clearFilters = async () => {
    setFilterValue("");
    setIsFilterApplied(false);
    setCurrentPage(1);
  };

  // pagination logic: server returns paginated data; adjust pages with totalCount
  useEffect(() => {
    const totalPages = itemsPerPage > 0 ? Math.ceil(totalCount / itemsPerPage) : 1;
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [totalCount, currentPage, itemsPerPage]);

  const handleDeleteClick = () => {
    const count = selectedIdsRef.current.length;
    if (count === 0) {
      toast.warning("Please select at least one policy to delete");
      return;
    }

    setDeleteMode(allSelected || count > 1 ? "multi" : "single");
    setDeleteId(allSelected ? null : selectedIdsRef.current[0]);
    onOpen();
  };

  // preserve selection when rowData updates
  useEffect(() => {
    if (preserveSelection && selectedIdsRef.current.length > 0 && gridRef.current?.api) {
      try {
        gridRef.current.api.forEachNode((node) => {
          if (selectedIdsRef.current.includes(node.data.id)) {
            node.setSelected(true);
          }
        });
      } catch (error) {
        console.error("Error in preserveSelection effect:", error);
      }
    }
  }, [rowData, currentPage, preserveSelection]);

  // paginatedData for ListView: server already paginates, so pass rowData
  const paginatedData = rowData;

  return (
    <div className="container p-4">
        <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-brandRed">Life Product Master List</h2>
        <div className="flex gap-2">
          <button
            className="px-4 py-2 addListTable-button"
            onClick={() => navigate("/life-product/add")}
          >
            Add
          </button>
          {selectedNames.length > 0 && (
            <button
              className="px-4 py-2 addListTable-button"
              onClick={handleDeleteClick}
            >
              Delete
            </button>
          )}
        </div>
      </div>

      <ListView
        filterColumn={filterColumn}
        setFilterColumn={setFilterColumn}
        columnOptions={columnOptions}
        filterValue={filterValue}
        setFilterValue={setFilterValue}
        handleFilter={handleFilter}
        clearFilters={clearFilters}
        isFilterApplied={isFilterApplied}
        gridRef={gridRef}
        columnDefs={columnDefs}
        rowData={rowData}
        paginatedData={paginatedData}
        itemsPerPage={itemsPerPage}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        onSelectionChanged={onSelectionChanged}
        defaultColDef={{ sortable: true, resizable: true, suppressMenu: true }}
        loading={loading}
        handleItemsPerPageChange={(e) => {
          const newSize = Number(e.target.value);
          setItemsPerPage(newSize);
          setCurrentPage(1);
        }}
        totalCount={totalCount} // if ListView supports totalCount you can pass it
          />

        {/* Selection action toolbar: appears when rows are selected (single or multiple) */}
        {selectedNames && selectedNames.length > 0 && (
          <div className="p-2 border-t border-gray-200 bg-white mt-2 rounded">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium">{selectedNames.length} selected</div>
              <div className="flex gap-2">
                <button
                  className="delete-button"
                  onClick={() => {
                    // open confirm modal for selected ids
                    setDeleteMode("multi");
                    onOpen({ ids: selectedIdsRef.current, names: selectedNames });
                  }}
                >
                  Delete
                </button>

                <button
                  className="backButton"
                  onClick={() => {
                    try {
                      if (gridRef.current?.api) gridRef.current.api.deselectAll();
                    } catch (e) {}
                    selectedIdsRef.current = [];
                    setSelectedNames([]);
                    setAllSelected(false);
                  }}
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        )}

     <ConfirmModal
        isOpen={isOpen}
        onClose={onClose}
        onConfirm={confirmDelete}
        title="Confirm Deletion"
        message={
          allSelected
            ? `Are you sure you want to delete all ${rowData.length} policies on this page?`
            : deleteMode === "multi"
              ? `Are you sure you want to delete ${selectedIdsRef.current.length} selected policies?`
              : "Are you sure you want to delete this policy?"
        }
        selectedNames={selectedNames}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        confirmColor="backButton"
      />
    </div>
  );
};

export default LifeProductMasterList;






